import axios from 'axios'

const BASE_URL = (process.env.VUE_APP_API_URL || 'https://fidmarket.fr/api').replace(/\/$/, '')

// Routes qui peuvent être mises en queue offline
const OFFLINE_ROUTES = [
  '/journee/vente-sans-fidelite',
  '/transactions',
]
const ACTIVE_DAY_KEY = 'journee_active_confirmed'

// ---- Queue offline ----
function getQueue() {
  try { return JSON.parse(localStorage.getItem('offline_queue') || '[]') } catch { return [] }
}
function saveQueue(q) {
  localStorage.setItem('offline_queue', JSON.stringify(q))
  window.dispatchEvent(new CustomEvent('offline-queue-updated', { detail: { count: q.length } }))
}
function addToQueue(config) {
  const q = getQueue()
  q.push({
    id:        Date.now() + Math.random(),
    method:    config.method,
    url:       config.url.replace(BASE_URL, ''),
    data:      config.data,
    headers:   config.headers,
    addedAt:   new Date().toISOString()
  })
  saveQueue(q)
}
function todayIso() {
  return new Date().toISOString().split('T')[0]
}
function parseData(data) {
  if (!data) return {}
  if (typeof data === 'string') {
    try { return JSON.parse(data) } catch { return {} }
  }
  return data
}
function hasConfirmedActiveDay(config) {
  const payload = parseData(config.data)
  const ville = String(payload.ville || '').trim()

  try {
    const confirmed = JSON.parse(localStorage.getItem(ACTIVE_DAY_KEY) || 'null')
    if (!confirmed?.active || confirmed.date !== todayIso()) return false
    if (!confirmed.journee_id) return false
    return String(confirmed.ville || '').trim() === ville
  } catch {
    return false
  }
}
export function getOfflineQueueCount() {
  return getQueue().length
}
export async function syncOfflineQueue() {
  const q = getQueue()
  if (!q.length) return { synced: 0, errors: 0 }

  const token = localStorage.getItem('token')
  let synced = 0, errors = 0
  const remaining = []

  for (const item of q) {
    try {
      await axios({
        method:  item.method,
        url:     BASE_URL + item.url,
        data:    typeof item.data === 'string' ? JSON.parse(item.data) : item.data,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 8000
      })
      synced++
    } catch (err) {
      // Si erreur réseau → remet en queue ; si erreur serveur → drop
      if (!err.response) {
        remaining.push(item)
      }
      errors++
    }
  }

  saveQueue(remaining)
  return { synced, errors }
}

// ---- Axios instance ----
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
})

// Ajoute token JWT
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Gestion réponses + offline
api.interceptors.response.use(
  response => response,
  error => {
    // Token expiré
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
      return Promise.reject(error)
    }

    // Erreur réseau (pas de réponse) → mise en queue si route éligible
    if (!error.response && error.config) {
      const url = error.config.url || ''
      const method = (error.config.method || '').toLowerCase()
      const isOfflineRoute = OFFLINE_ROUTES.some(r => url.includes(r))

      if (method === 'post' && isOfflineRoute) {
        if (!hasConfirmedActiveDay(error.config)) {
          return Promise.reject({
            response: {
              status: 409,
              data: { error: 'Impossible d’enregistrer hors ligne : aucune journée active confirmée.' }
            },
            config: error.config
          })
        }
        addToQueue(error.config)
        // Retourner une réponse simulée pour ne pas bloquer l'UI
        return Promise.resolve({
          data: { offline: true, message: 'Enregistré hors ligne' },
          status: 202,
          offline: true
        })
      }
    }

    return Promise.reject(error)
  }
)

// Synchro automatique au retour réseau
window.addEventListener('online', async () => {
  const q = getQueue()
  if (q.length > 0) {
    setTimeout(async () => {
      const result = await syncOfflineQueue()
      window.dispatchEvent(new CustomEvent('offline-sync-done', { detail: result }))
    }, 1500) // Petit délai pour laisser le réseau se stabiliser
  }
})

export default api
